import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

// Public web config — safe to expose. Filled from frontend/.env (VITE_FIREBASE_*).
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

// If the keys aren't set, the whole push layer stays disabled (no crashes).
export const isFcmConfigured = Boolean(
  firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.messagingSenderId && VAPID_KEY
);

let messaging = null;
let initTried = false;

async function getMessagingInstance() {
  if (messaging) return messaging;
  if (initTried) return null;
  initTried = true;
  if (!isFcmConfigured) return null;
  const supported = await isSupported().catch(() => false);
  if (!supported) return null;
  const app = initializeApp(firebaseConfig);
  messaging = getMessaging(app);
  return messaging;
}

// Ask permission, register the messaging service worker, return an FCM token (or null).
export async function requestFcmToken() {
  try {
    if (!('serviceWorker' in navigator) || !('Notification' in window)) return null;
    const inst = await getMessagingInstance();
    if (!inst) return null;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const token = await getToken(inst, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    return token || null;
  } catch (err) {
    console.warn('[FCM] requestFcmToken failed:', err?.message || err);
    return null;
  }
}

// Subscribe to foreground messages (app tab open). Returns an unsubscribe fn.
export async function onForegroundMessage(cb) {
  const inst = await getMessagingInstance();
  if (!inst) return () => {};
  return onMessage(inst, (payload) => {
    const n = payload?.notification || {};
    const d = payload?.data || {};
    cb({
      title: n.title || d.title || 'Notification',
      body: n.body || d.body || '',
      link: d.link || '',
      type: d.type || 'info',
    });
  });
}
